import { DateRange } from '@robocorp/components';
import {
  FailedWorkItemExceptionType,
  WorkItemBulkOperationType,
} from '@robocorp/robocloud-orchestrator-interface';
import { Cursor, SearchResponse, WorkItem } from '@robocorp/robocloud-search-iternal-sdk';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { TimeRangeValues } from '~/constants/timeRanges';
import * as searchService from '~/services/searchAPI';
import { WorkItemQueryArgs } from '~/services/searchAPI';
import { workItemService } from '~/services/taskDataAPI';
import {
  DataExplorerFilters,
  WorkItemDTO,
  WorkItemPayload,
  WorkItemState,
  workItemStateShortToLongMap,
} from '~/types/resources';
import { WithWorkspaceId } from '~/types/common';
import { useLazyQuery } from '../useLazyQuery';
import { getWorkItemSummaryQueryKey } from './useWorkItemSummaryQuery';
import {
  withSearchSyncDelay,
  formatErrorPayload,
  validateApiResponse,
  UseApiQueryOptions,
  UseApiMutationOptions,
  useCurrentWorkspaceQueryKey,
} from '../utils';

type WorkItemsArgs =
  | {
      scope: 'process';
      processId: string;
    }
  | {
      scope: 'processRun';
      processId: string;
      processRunId: string;
    }
  | {
      scope: 'step';
      processId: string;
      stepId: string;
    };

export type WorkItemsQueryStringParams = Omit<
  WorkItemQueryArgs,
  'processRunId' | 'activityId' | 'byStepState'
>;

export interface WorkItemsFilters extends WorkItemsQueryStringParams {
  from?: number;
  cursor?: Cursor;
  createTs?: TimeRangeValues;
  customDateRange?: DateRange;
  exceptionType?: FailedWorkItemExceptionType;
  state?: WorkItemState[];
  activityId?: string[];
}

export type WorkItemSearchArgs = {
  queryArgs?: WorkItemsFilters;
  filters?: DataExplorerFilters;
};

export type FetchWorkItemSearchArgs = WorkItemSearchArgs & WorkItemsArgs & WithWorkspaceId;
export type FetchWorkItemSearchQueryArgs = WorkItemSearchArgs & WorkItemsArgs;

export const getWorkItemsQueryKey = ({ scope, ...rest }: WorkItemsArgs) => [
  'workItems',
  { scope },
  rest,
];
export const getWorkItemQueryKey = (args: { workItemId: string }) => ['workItem', args];
const getWorkItemLogQueryKey = (args: { workItemId: string }) => ['workItem', 'log', args];

export enum WorkItemsQueryErrors {
  GET_WORK_ITEM_ERROR = 'GET_WORK_ITEM_ERROR',
  GET_WORK_ITEMS_ERROR = 'GET_WORK_ITEMS_ERROR',
  DISCARD_WORK_ITEM_ERROR = 'DISCARD_WORK_ITEM_ERROR',
  DELETE_WORK_ITEM_ERROR = 'DELETE_WORK_ITEM_ERROR',
  CREATE_WORK_ITEM_ERROR = 'CREATE_WORK_ITEM_ERROR',
  UPDATE_WORK_ITEM_DATA_ERROR = 'UPDATE_WORK_ITEM_DATA_ERROR',
  DELETE_WORK_ITEM_DATA_ERROR = 'DELETE_WORK_ITEM_DATA_ERROR',
  RETRY_WORK_ITEMS_ERROR = 'RETRY_WORK_ITEMS_ERROR',
  GET_WORK_ITEM_DOWNLOAD_LINK_ERROR = 'GET_WORK_ITEM_DOWNLOAD_LINK_ERROR',
  GET_WORK_ITEM_UPLOAD_LINK_ERROR = 'GET_WORK_ITEM_UPLOAD_LINK_ERROR',
  UPLOAD_WORK_ITEM_FILE_ERROR = 'UPLOAD_WORK_ITEM_FILE_ERROR',
  UPLOAD_NEW_WORK_ITEM_FILE_ERROR = 'UPLOAD_NEW_WORK_ITEM_FILE_ERROR',
  DELETE_WORK_ITEM_FILE_ERROR = 'DELETE_WORK_ITEM_FILE_ERROR',
}

export const defaultWorkItemsData = {
  data: [],
  total: 0,
  previousCursor: null,
  nextCursor: null,
};

const parseWorkItemSearchArgs = (args: WorkItemSearchArgs): WorkItemQueryArgs => ({
  size: 25,
  includeData: true,
  activityId: args.filters?.activityId,
  activityRunId: args.filters?.activityRunId,
  state: args.filters?.state?.map((shortState) => workItemStateShortToLongMap[shortState]),
  ...(args.queryArgs ?? {}),
});

export const fetchWorkItems =
  ({ workspaceId, ...args }: FetchWorkItemSearchArgs) =>
  async () => {
    const queryArgs: WorkItemQueryArgs = parseWorkItemSearchArgs(args);
    const apiRequest = () => {
      if (args.scope === 'step') {
        return searchService.getWorkItems(
          { workspaceId, processId: args.processId },
          {
            activityId: [args.stepId],
            ...queryArgs,
          },
        );
      }
      if (args.scope === 'processRun') {
        return searchService.getWorkItems(
          { workspaceId, processId: args.processId },
          {
            processRunId: args.processRunId,
            ...queryArgs,
          },
        );
      }
      return searchService.getWorkItems({ workspaceId, processId: args.processId }, queryArgs);
    };

    const response = validateApiResponse(await apiRequest());

    if (!response.data) throw new Error('Empty data');
    return response.data;
  };

export const useWorkItemsQuery = (args: FetchWorkItemSearchQueryArgs) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getWorkItemsQueryKey(args),
    async () => fetchWorkItems({ ...args, workspaceId })(),
    {
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkItemsQueryErrors.GET_WORK_ITEMS_ERROR,
          }),
        );
      },
      refetchOnWindowFocus: true,
      keepPreviousData: true,
    },
  );
};

export const useWorkItemsLazyQuery = () => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useLazyQuery<FetchWorkItemSearchQueryArgs, SearchResponse<WorkItem>, Error>(
    (args) => getWorkItemsQueryKey(args),
    (args) => fetchWorkItems({ ...args, workspaceId }),
    {
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkItemsQueryErrors.GET_WORK_ITEMS_ERROR,
          }),
        );
      },
      refetchOnWindowFocus: true,
      keepPreviousData: true,
    },
  );
};

export const useWorkItemQuery = (
  { processId, workItemId }: { processId: string; workItemId: string },
  options?: UseApiQueryOptions<typeof workItemService.getProcessWorkItemById>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getWorkItemQueryKey({ workItemId }),
    async () => {
      const response = validateApiResponse(
        await workItemService.getProcessWorkItemById({
          workspaceId,
          pid: processId,
          id: workItemId,
          includeData: true,
        }),
      );
      if (!response.data) throw new Error('Empty data');
      return response.data;
    },
    {
      onError: (error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkItemsQueryErrors.GET_WORK_ITEM_ERROR,
          }),
        );
      },
      ...options,
    },
  );
};

export const useCreateWorkItemMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId, payload }: { processId: string; payload: WorkItemPayload }) => {
      const response = validateApiResponse(
        await workItemService.createWorkItem({ workspaceId, pid: processId, payload }),
      );

      if (!response.data) throw new Error('Empty data');
      return response.data;
    },
    {
      onSuccess: (workItem) => {
        withSearchSyncDelay(() => {
          queryClient.setQueryData(getWorkItemQueryKey({ workItemId: workItem.id }), workItem);
          queryClient.invalidateQueries(
            getWorkItemSummaryQueryKey({ processId: workItem.processId }),
          );
          queryClient.invalidateQueries(
            getWorkItemsQueryKey({
              scope: 'process',
              processId: workItem.processId,
            }),
          );
        });
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkItemsQueryErrors.CREATE_WORK_ITEM_ERROR,
          }),
        );
      },
    },
  );
};

export const useUpdateWorkItemDataMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ workItemId, payload }: { workItemId: string; payload: WorkItemPayload }) => {
      const response = validateApiResponse(
        await workItemService.updateWorkItemPayload({ workspaceId, workItemId, payload }),
      );

      if (!response.data) throw new Error('Empty data');
      return { workItemId, payload };
    },
    {
      onSuccess: ({ workItemId, payload }) => {
        withSearchSyncDelay(() => {
          queryClient.setQueryData(
            getWorkItemQueryKey({ workItemId }),
            (item: WorkItemDTO | undefined) => {
              return { ...item, payload } as WorkItemDTO;
            },
          );
          queryClient.invalidateQueries(['workItems']);
        });
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkItemsQueryErrors.UPDATE_WORK_ITEM_DATA_ERROR,
          }),
        );
      },
    },
  );
};

export const useDeleteWorkItemDataMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ workItemId }: { workItemId: string }) => {
      validateApiResponse(await workItemService.deleteWorkItemPayload({ workspaceId, workItemId }));
      return workItemId;
    },
    {
      onSuccess: (workItemId) => {
        withSearchSyncDelay(() => {
          queryClient.setQueryData(
            getWorkItemQueryKey({ workItemId }),
            (item: WorkItemDTO | undefined) => {
              return { ...item, payload: undefined } as WorkItemDTO;
            },
          );
          queryClient.invalidateQueries(['workItems']);
        });
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkItemsQueryErrors.DELETE_WORK_ITEM_DATA_ERROR,
          }),
        );
      },
    },
  );
};

export const useRetryWorkItemsMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId, workItemIds }: { processId: string; workItemIds: string[] }) => {
      const responses = await Promise.all(
        workItemIds.map(async (workItemId) =>
          workItemService.retryWorkItem({ workspaceId, processId, workItemId }),
        ),
      );

      const firstFailed = responses.find((response) => !response.ok);
      if (firstFailed?.problem) validateApiResponse(firstFailed);

      return responses.map((response) => (response.ok ? response.data : undefined));
    },
    {
      onSuccess: (data) => {
        if (!data) {
          return;
        }

        withSearchSyncDelay(() => {
          const processIds: string[] = [];
          const processRunIds: string[] = [];
          const stepIds: string[] = [];

          data.forEach((workItem) => {
            if (!workItem) {
              return;
            }

            queryClient.invalidateQueries(getWorkItemQueryKey({ workItemId: workItem.id }));

            if (processIds.indexOf(workItem.processId) < 0) {
              queryClient.invalidateQueries(
                getWorkItemsQueryKey({ scope: 'process', processId: workItem.processId }),
              );
              processIds.push(workItem.processId);
            }

            if (workItem.processRunId && processRunIds.indexOf(workItem.processRunId) < 0) {
              queryClient.invalidateQueries(
                getWorkItemsQueryKey({
                  scope: 'processRun',
                  processId: workItem.processId,
                  processRunId: workItem.processRunId,
                }),
              );
              processRunIds.push(workItem.processRunId);
            }

            if (workItem.activityId && processRunIds.indexOf(workItem.activityId) < 0) {
              queryClient.invalidateQueries(
                getWorkItemsQueryKey({
                  scope: 'step',
                  processId: workItem.processId,
                  stepId: workItem.activityId,
                }),
              );
              stepIds.push(workItem.activityId);
            }
          });
        });
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkItemsQueryErrors.RETRY_WORK_ITEMS_ERROR,
          }),
        );
      },
    },
  );
};

export const useDeleteWorkItemsMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId, workItemIds }: { processId: string; workItemIds: string[] }) => {
      const responses = await Promise.all(
        workItemIds.map(async (workItemId) =>
          workItemService.deleteWorkItem({ workspaceId, processId, workItemId }),
        ),
      );

      const firstFailed = responses.find((response) => !response.ok);
      if (firstFailed?.problem) validateApiResponse(firstFailed);

      return responses.map((response) => (response.ok ? response.data : undefined));
    },
    {
      onSuccess: (_, { processId, workItemIds }) => {
        workItemIds.forEach((id) => {
          queryClient.invalidateQueries(getWorkItemQueryKey({ workItemId: id }));
        });

        queryClient.setQueriesData(
          ['workItems', {}, { processId }],
          (previousData: { data: { id: string }[] } | undefined) => {
            if (!previousData) {
              return previousData;
            }

            return {
              ...previousData,
              data: (previousData.data ?? []).filter(({ id }) => !workItemIds.includes(id)),
            };
          },
        );
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkItemsQueryErrors.DELETE_WORK_ITEM_ERROR,
          }),
        );
      },
    },
  );
};

export const useDiscardWorkItemsMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ processId, workItemIds }: { processId: string; workItemIds: string[] }) => {
      const responses = await Promise.all(
        workItemIds.map(async (workItemId) =>
          workItemService.discardWorkItem({ workspaceId, processId, workItemId }),
        ),
      );

      const firstFailed = responses.find((response) => !response.ok);
      if (firstFailed?.problem) validateApiResponse(firstFailed);

      return responses.map((response) => (response.ok ? response.data : undefined));
    },
    {
      onSuccess: (data) => {
        if (!data) {
          return;
        }

        withSearchSyncDelay(() => {
          const processIds: string[] = [];
          const processRunIds: string[] = [];
          const stepIds: string[] = [];

          data.forEach((workItem) => {
            if (!workItem) {
              return;
            }

            queryClient.invalidateQueries(getWorkItemQueryKey({ workItemId: workItem.id }));

            if (processIds.indexOf(workItem.processId) < 0) {
              queryClient.invalidateQueries(
                getWorkItemsQueryKey({ scope: 'process', processId: workItem.processId }),
              );
              processIds.push(workItem.processId);
            }

            if (workItem.processRunId && processRunIds.indexOf(workItem.processRunId) < 0) {
              queryClient.invalidateQueries(
                getWorkItemsQueryKey({
                  scope: 'processRun',
                  processId: workItem.processId,
                  processRunId: workItem.processRunId,
                }),
              );
              processRunIds.push(workItem.processRunId);
            }

            if (workItem.activityId && processRunIds.indexOf(workItem.activityId) < 0) {
              queryClient.invalidateQueries(
                getWorkItemsQueryKey({
                  scope: 'step',
                  processId: workItem.processId,
                  stepId: workItem.activityId,
                }),
              );
              stepIds.push(workItem.activityId);
            }
          });
        });
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkItemsQueryErrors.DISCARD_WORK_ITEM_ERROR,
          }),
        );
      },
    },
  );
};

export const useDeleteWorkItemFileMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ workItemId, fileId }: { workItemId: string; fileId: string }) => {
      validateApiResponse(
        await workItemService.deleteWorkItemFile({ workspaceId, workItemId, fileId }),
      );
      return workItemId;
    },
    {
      onSuccess: (workItemId) => {
        withSearchSyncDelay(() => {
          queryClient.invalidateQueries(getWorkItemQueryKey({ workItemId }));
          queryClient.invalidateQueries(['workItems']);
        });
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkItemsQueryErrors.DELETE_WORK_ITEM_FILE_ERROR,
          }),
        );
      },
    },
  );
};

export const useWorkItemDownloadLinkMutation = () => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ workItemId, fileId }: { workItemId: string; fileId: string }) => {
      const response = validateApiResponse(
        await workItemService.getWorkItemFileDownloadLink({ workspaceId, workItemId, fileId }),
      );
      if (!response.data) throw new Error('Empty data');

      return response.data;
    },
    {
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkItemsQueryErrors.GET_WORK_ITEM_DOWNLOAD_LINK_ERROR,
          }),
        );
      },
    },
  );
};

export const useUploadWorkItemFileMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ workItemId, file }: { workItemId: string; file: File; isNewWorkItem?: boolean }) => {
      const linkResponse = validateApiResponse(
        await workItemService.getWorkItemFileUploadLink({
          workspaceId,
          workItemId,
          fileName: file.name,
          fileSize: file.size,
        }),
      );
      if (!linkResponse.data) throw new Error('Empty data');

      const { fields, url } = linkResponse.data;
      const form = new FormData();

      Object.entries(fields).forEach(([key, value]) => form.append(key, value as string | Blob));
      form.append('file', file);

      validateApiResponse(await workItemService.uploadWorkItemFile(url, form));
      return workItemId;
    },
    {
      onSuccess: (workItemId) => {
        withSearchSyncDelay(() => {
          queryClient.invalidateQueries(getWorkItemQueryKey({ workItemId }));
          queryClient.invalidateQueries(['workItems']);
        });
      },
      onError: (error: Error, { isNewWorkItem }) => {
        dispatch(
          formatErrorPayload(error, {
            type: isNewWorkItem
              ? WorkItemsQueryErrors.UPLOAD_NEW_WORK_ITEM_FILE_ERROR
              : WorkItemsQueryErrors.UPLOAD_WORK_ITEM_FILE_ERROR,
          }),
        );
      },
    },
  );
};

export const useTriggerWorkItemBulkOperationMutation = (
  options?: UseApiMutationOptions<typeof workItemService.triggerWorkItemBulkOperation>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({
      processId,
      operation,
      ...restParams
    }:
      | {
          processId: string;
          operation: WorkItemBulkOperationType;
          workItemIds: string[];
        }
      | {
          processId: string;
          operation: WorkItemBulkOperationType;
          filters: WorkItemSearchArgs;
        }) => {
      const params =
        'workItemIds' in restParams
          ? { workItemIds: restParams.workItemIds }
          : { filters: parseWorkItemSearchArgs(restParams.filters) };

      return validateApiResponse(
        await workItemService.triggerWorkItemBulkOperation({
          workspaceId,
          processId,
          args: { operation, ...params },
        }),
      ).data;
    },
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useWorkItemLogQuery = (
  id: string,
  options?: UseApiQueryOptions<typeof workItemService.getWorkItemEventLog>,
) => {
  const dispatch = useDispatch();

  const { workspaceId, queryKeyFormatter } = useCurrentWorkspaceQueryKey();

  return useQuery(
    queryKeyFormatter(getWorkItemLogQueryKey({ workItemId: id })),
    async () =>
      validateApiResponse(
        await workItemService.getWorkItemEventLog({ workspaceId, workItemId: id }),
      ).data,
    {
      onError: (error) => {
        dispatch(formatErrorPayload(error));
      },
      ...options,
    },
  );
};
